import React, { useState, useEffect, useContext } from 'react';
import { Form } from 'react-bootstrap';
import AppContext from 'contexts/appContext';
import Panel from 'components/common/panel/panel';
import Inputs from 'components/inputs/inputs';
import { ShowConfirmPopup } from 'components/common/popup/popup';
import useForm from 'hooks/form';

export default function OrdersCreatePage(props) {
  const { setAppArea, setAppBreadcrumb } = useContext(AppContext);

  useEffect(() => {
    setAppArea(AppContext.Areas.Orders, 'Order Setup');
    setAppBreadcrumb([{ name: 'Home', href: '/' }, { name: 'Orders', href: '/Orders' }, { name: 'Create Order', selected: true }]);
  }, []);

  return (
    <OrdersCreateForm />
  );
}

function OrdersCreateForm(props) {
  const { setFormRef, submitForm, renderFormValidationMessage } = useForm({ protect: true });

  const [data, setData] = useState(initialData);

  const storeData = data.stores;
  const enterpriseData = data.enterprise;
  const userData = data.user;
  const notesData = data.notes;

  const setStoreData = (newStoreData, index) => {
    const stores = [...data.stores];
    stores[index] = newStoreData;
    setData({
      ...data,
      stores: stores,
    });
  };

  const setEnterpriseData = (newEnterpriseData) => {
    setData({
      ...data,
      enterprise: newEnterpriseData,
    });
  };

  const setUserData = (newUserData) => {
    setData({
      ...data,
      user: newUserData,
    });
  };

  const setNotesData = (newNotesData) => {
    setData({
      ...data,
      notes: newNotesData,
    });
  };

  const addStore = () => {
    setData({
      ...data,
      stores: [...data.stores, { ...emptyStore }],
    });
  };

  const removeStore = (index) => {
    const stores = [...data.stores];
    stores.splice(index, 1)
    setData({
      ...data,
      stores: stores,
    });
  };

  const handleRemoveStore = (index) => {
    ShowConfirmPopup({
      title: 'Confirm',
      message: <div className='py-2'>Are you sure you want to remove this store?</div>,
      acceptLabel: 'Yes, Remove',
      acceptIconClass: 'icons8-trash',
      acceptVariant: 'danger',
      declineLabel: 'No',
      declineIconClass: 'icons8-delete',
      declineVariant: 'default',
      onAccept: () => {
        removeStore(index);
      }
    });
  };

  const updateField = (name, value) => {
    const newData = { ...data };
    newData[name] = value;
    setData(newData);
  };

  const handleCancel = () => {
    props.history.goBack();
  };

  const handleSubmit = submitForm(() => {

  });

  return (
    <Form ref={setFormRef} noValidate>

      <div className='row border-bottom mt-4 mx-4'>
        <div className='col-12 col-md-4'>
          <h3 className='text-thin m-3'><i className='icons8-control-panel mr-2' /> Application</h3>
        </div>
        <div className='col-12 col-md-8'>
          <Panel>
            <Panel.Body>
              <Form.Group>
                <Inputs.Checkbox label='POS including any POSitouch optional module' value={data.isOrderedPOS} onValueChange={(v) => updateField('isOrderedPOS', v)} block />
                <Inputs.Checkbox label='POSiCloud Reporting' value={data.isOrderedReporting} onValueChange={(v) => updateField('isOrderedReporting', v)} block />
                <Inputs.Checkbox label='POSiCloud POSiGuestPay' value={data.isOrderedPGP} onValueChange={(v) => updateField('isOrderedPGP', v)} block />
                <Inputs.Checkbox label='POSiCloud QME' value={data.isOrderedQME} onValueChange={(v) => updateField('isOrderedQME', v)} block />
                <Inputs.Checkbox label='POSiCloud Dashboard' value={data.isOrderedDashboard} onValueChange={(v) => updateField('isOrderedDashboard', v)} block />
              </Form.Group>
            </Panel.Body>
          </Panel>
        </div>
      </div>

      <div className='row border-bottom mt-4 mx-4'>
        <div className='col-12 col-md-4'>
          <h3 className='text-thin m-3'><i className='icons8-document mr-2' /> Dealer</h3>
        </div>
        <div className='col-12 col-md-8'>
          <Panel>
            <Panel.Body>
              <Form.Group>
                <Form.Label>Dealership</Form.Label>
                <Inputs.Plaintext value='AM/PM Service (Arizona)' />
              </Form.Group>
              <Form.Group>
                <Form.Label>Sales Contact Name</Form.Label>
                <Inputs.Text placeholder='Please enter sales contact name' />
              </Form.Group>
              <Form.Group>
                <Form.Label>Sales Contact Email</Form.Label>
                <Inputs.Email placeholder='Please enter sales contact email' />
              </Form.Group>
              <Form.Group>
                <Form.Label>Sales Contact Phone</Form.Label>
                <Inputs.Phone placeholder='Please enter sales contact phone' />
              </Form.Group>
            </Panel.Body>
          </Panel>
        </div>
      </div>

      <div className='row border-bottom mt-4 mx-4'>
        <div className='col-12 col-md-4'>
          <div className='m-3'>
            <h3 className='text-thin'><i className='icons8-home mr-2' /> Stores</h3>
            <Inputs.Button variant='primary' size='md' onClick={addStore} iconClass='icons8-plus-math' label='Add Another' />
          </div>
        </div>
        <div className='col-12 col-md-8'>
          {
            storeData.map((item, index) => {
              return (
                <Panel key={index}>
                  <Panel.Header tools={<Inputs.Button variant='hover-danger' iconClass='icons8-trash' onClick={() => handleRemoveStore(index)} />}>
                    Store {index + 1} of {storeData.length}
                  </Panel.Header>
                  <Panel.Body>
                    <StoreForm data={item} setData={setStoreData} index={index} editable />
                  </Panel.Body>
                </Panel>
              );
            })
          }
        </div>
      </div>

      <div className='row border-bottom mt-4 mx-4'>
        <div className='col-12 col-md-4'>
          <h3 className='text-thin m-3'><i className='icons8-office mr-2' /> Enterprise</h3>
        </div>
        <div className='col-12 col-md-8'>
          <Panel>
            <Panel.Body>
              <EnterpriseForm data={enterpriseData} setData={setEnterpriseData} editable />
            </Panel.Body>
          </Panel>
        </div>
      </div>

      <div className='row border-bottom mt-4 mx-4'>
        <div className='col-12 col-md-4'>
          <h3 className='text-thin m-3'><i className='icons8-user mr-2' /> Administrator</h3>
        </div>
        <div className='col-12 col-md-8'>
          <Panel>
            <Panel.Body>
              <UserForm data={userData} setData={setUserData} editable />
            </Panel.Body>
          </Panel>
        </div>
      </div>

      <div className='row border-bottom mt-4 mx-4'>
        <div className='col-12 col-md-4'>
          <h3 className='text-thin m-3'><i className='icons8-open-book mr-2' /> Notes</h3>
        </div>
        <div className='col-12 col-md-8'>
          <Panel>
            <Panel.Body>
              <NotesForm data={notesData} setData={setNotesData} editable />
            </Panel.Body>
          </Panel>
        </div>
      </div>

      <div className='row my-4 mx-4'>
        <div className='col-12 col-md-4'>
          <h3 className='text-thin m-3'><i className='icons8-checkmark mr-2' /> Confirm</h3>
        </div>
        <div className='col-12 col-md-8 mt-2'>
          {renderFormValidationMessage()}
          <Inputs.Button variant='default' onClick={handleCancel} className='ml-2' iconClass='icons8-undo' label='Cancel' />
          <Inputs.Button variant='success' onClick={handleSubmit} className='ml-2' iconClass='icons8-save' label='Submit' />
        </div>
      </div>

    </Form>
  );
}

function StoreForm(props) {
  const { data, setData, index } = props;

  const updateField = (name, value) => {
    const newData = { ...data };
    newData[name] = value;
    setData(newData, index);
  };

  return (
    <>
      <Form.Group>
        <Form.Label>POSitouch Key Number <span className='subtle'>(if existing)</span></Form.Label>
        <Inputs.Text placeholder='Please enter key number' value={data.keyId} onValueChange={(v) => updateField('keyId', v)} />
      </Form.Group>

      <Form.Row>
        <Form.Group className='col-md-8'>
          <Form.Label required>Store Name</Form.Label>
          <Inputs.Text required placeholder='Please enter store name' value={data.name} onValueChange={(v) => updateField('name', v)} />
        </Form.Group>
        <Form.Group className='col-md-4 mb-3'>
          <Form.Label>Store Number</Form.Label>
          <Inputs.Text placeholder='Please enter store number' value={data.number} onValueChange={(v) => updateField('number', v)} />
        </Form.Group>
      </Form.Row>
      <Form.Group>
        <Form.Label>Phone</Form.Label>
        <Inputs.Phone placeholder='Please enter store phone' value={data.phone} onValueChange={(v) => updateField('phone', v)} />
      </Form.Group>

      <Form.Group>
        <Form.Label>Address</Form.Label>
        <Inputs.Text placeholder='Please enter street address' value={data.address} onValueChange={(v) => updateField('address', v)} />
      </Form.Group>
      <Form.Group>
        <Form.Label>Address 2</Form.Label>
        <Inputs.Text placeholder='Please enter address line 2' value={data.address2} onValueChange={(v) => updateField('address2', v)} />
      </Form.Group>
      <Form.Row>
        <Form.Group className='col-md-6'>
          <Form.Label>City</Form.Label>
          <Inputs.Text placeholder='Please enter city' value={data.city} onValueChange={(v) => updateField('city', v)} />
        </Form.Group>
        <Form.Group className='col-md-4'>
          <Form.Label>State</Form.Label>
          <Inputs.Text placeholder='Please enter state' value={data.state} onValueChange={(v) => updateField('state', v)} />
        </Form.Group>
        <Form.Group className='col-md-2'>
          <Form.Label>Zip</Form.Label>
          <Inputs.Text placeholder='Please enter zip' value={data.zip} onValueChange={(v) => updateField('zip', v)} />
        </Form.Group>
      </Form.Row>
    </>
  );
}

function EnterpriseForm(props) {
  const { data, setData } = props;

  const updateField = (name, value) => {
    const newData = { ...data };
    newData[name] = value;
    setData(newData);
  };

  return (
    <>
      <Form.Group>
        <Form.Label required>Enterprise Name</Form.Label>
        <Inputs.Text required placeholder='Please enter enterprise name' value={data.name} onValueChange={(v) => updateField('name', v)} />
      </Form.Group>
      <Form.Group>
        <Form.Label>Phone</Form.Label>
        <Inputs.Phone placeholder='Please enter enterprise phone' value={data.phone} onValueChange={(v) => updateField('phone', v)} />
      </Form.Group>

      <Form.Group>
        <Form.Label>Address</Form.Label>
        <Inputs.Text placeholder='Please enter street address' value={data.address} onValueChange={(v) => updateField('address', v)} />
      </Form.Group>
      <Form.Group>
        <Form.Label>Address 2</Form.Label>
        <Inputs.Text placeholder='Please enter address line 2' value={data.address2} onValueChange={(v) => updateField('address2', v)} />
      </Form.Group>
      <Form.Row>
        <Form.Group className='col-md-6'>
          <Form.Label>City</Form.Label>
          <Inputs.Text placeholder='Please enter city' value={data.city} onValueChange={(v) => updateField('city', v)} />
        </Form.Group>
        <Form.Group className='col-md-4'>
          <Form.Label>State</Form.Label>
          <Inputs.Text placeholder='Please enter state' value={data.state} onValueChange={(v) => updateField('state', v)} />
        </Form.Group>
        <Form.Group className='col-md-2'>
          <Form.Label>Zip</Form.Label>
          <Inputs.Text placeholder='Please enter zip' value={data.zip} onValueChange={(v) => updateField('zip', v)} />
        </Form.Group>
      </Form.Row>
    </>
  );
}

function UserForm(props) {
  const { data, setData } = props;

  const updateField = (name, value) => {
    const newData = { ...data };
    newData[name] = value;
    setData(newData);
  };

  const getSuggestedUserId = () => {
    const firstName = (data.firstName || '').replace(/\s/g, '').toLowerCase();
    const lastName = (data.lastName || '').replace(/\s/g, '').toLowerCase();
    const suggested = `${(firstName.length > 0 ? firstName[0] : '')}${lastName}`;
    return (
      <>
        <span className='mr-1'>Suggested:</span>
        <Inputs.Button
          variant='link'
          size='xs'
          label={suggested}
          onClick={() => updateField('desiredUserId', suggested)}
          style={{ lineHeight: 1.3, verticalAlign: 'top', margin: 0, padding: 0 }}
        />
      </>
    );
  };

  return (
    <>
      <Form.Row>
        <Form.Group className='col-md-6'>
          <Form.Label required>First Name</Form.Label>
          <Inputs.Text required placeholder='Please enter first name' value={data.firstName} onValueChange={(v) => updateField('firstName', v)} />
        </Form.Group>
        <Form.Group className='col-md-6 mb-3'>
          <Form.Label required>Last Name</Form.Label>
          <Inputs.Text required placeholder='Please enter last name' value={data.lastName} onValueChange={(v) => updateField('lastName', v)} />
        </Form.Group>
      </Form.Row>
      <Form.Group>
        <Form.Label>Phone</Form.Label>
        <Inputs.Phone placeholder='Please enter user phone' value={data.phone} onValueChange={(v) => updateField('phone', v)} />
      </Form.Group>
      <Form.Group>
        <Form.Label>Email</Form.Label>
        <Inputs.Email placeholder='Please enter user email' value={data.email} onValueChange={(v) => updateField('email', v)} />
      </Form.Group>
      <Form.Group>
        <Form.Label>Desired User Id</Form.Label>
        <Inputs.Text placeholder='Please enter user id' value={data.desiredUserId} onValueChange={(v) => updateField('desiredUserId', v)} help={getSuggestedUserId()} />
      </Form.Group>
    </>
  );
}

function NotesForm(props) {
  const { data, setData } = props;

  const updateField = (name, value) => {
    const newData = { ...data };
    newData[name] = value;
    setData(newData);
  };

  return (
    <>
      <Form.Group>
        <Form.Label>Special Instructions</Form.Label>
        <Inputs.TextArea placeholder='Please enter any special instructions' value={data.instructions} onValueChange={(v) => updateField('instructions', v)} rows='6' />
      </Form.Group>
    </>
  );
}

const emptyStore = {
  keyId: null,
  name: null,
  number: null,
  phone: null,
  address: null,
  address2: null,
  city: null,
  state: null,
  zip: null,
};

const emptyEnterprise = {
  name: null,
  phone: null,
  address: null,
  address2: null,
  city: null,
  state: null,
  zip: null,
};

const emptyUser = {
  firstName: null,
  lastName: null,
  phone: null,
  email: null,
  desiredUserId: null,
};

const emptyNotes = {
  instructions: null,
};

const initialData = {
  dealerId: null,
  isOrderedPOS: false,
  isOrderedReporting: false,
  isOrderedPGP: false,
  isOrderedQME: false,
  isOrderedDashboard: false,
  stores: [{ ...emptyStore }],
  enterprise: { ...emptyEnterprise },
  user: { ...emptyUser },
  notes: { ...emptyNotes },
};